import {AxiosInstance} from "axios";
import {action, makeAutoObservable} from "mobx";
import {IResult} from "../services/Result";
import {AxiosResponseHelper} from "../utilities/AxiosResponseHelper";
import {IAnalyticsStore} from "./AnalyticsStore";
import { ITour } from "../types/models/Tour";
import { ITourAssignment } from "../types/models/TourAssignment";
import { IOrganizationTour } from "../types/models/OrganizationTour";

export interface IToursStore {
  tours: ITour[];
  selectedOrganizationId: string;
  selectedOrganizationTourAssignments: ITourAssignment[];
  selectedOrganizationTours: IOrganizationTour[];
  isLoading: boolean;
  isUpdating: boolean;
  error: { title: string; message: string };
  getOrganizationTours: (apiInstance: AxiosInstance) => Promise<void>;
  setSelectedOrganizationId: (organizationId: string) => void;
  addTourAssignment: (apiInstance: AxiosInstance, tourId: string) => Promise<void>;
  deleteTourAssignment: (apiInstance: AxiosInstance, tourId: string) => Promise<void>;
}

export class ToursStore implements IToursStore {
  tours: ITour[] = [];
  selectedOrganizationId: string = '';
  selectedOrganizationTourAssignments: ITourAssignment[] = [];
  selectedOrganizationTours: IOrganizationTour[] = [];

  isLoading: boolean = false;
  isUpdating: boolean = false;

  error: { title: string; message: string };

  _analytics_store: IAnalyticsStore;

  constructor(analytics_store: IAnalyticsStore) {
    this._analytics_store = analytics_store;
    makeAutoObservable(this);  
  }

  @action
  private async getTours(apiInstance: AxiosInstance){
    const response = await apiInstance.get<IResult<ITour[]>>('api/Tours');
        
    if (AxiosResponseHelper.IsSuccessfulResponse(response) && response.data.data){
      this.tours = response.data.data;
    } else {
      let errorMsg = "Cannot retrieve list of tours"
      AxiosResponseHelper.ReportErrors(this._analytics_store, errorMsg, response.data)
    }
  }

  @action
  private async getOrganizationTourAssignments(apiInstance: AxiosInstance) {
    const response = await apiInstance.get<IResult<ITourAssignment[]>>(`api/Tours/organization/${this.selectedOrganizationId}`);

    if (AxiosResponseHelper.IsSuccessfulResponse(response) && response.data.data) {
      this.selectedOrganizationTourAssignments = response.data.data;
    } else {
      let errorMsg = "Cannot retrieve list of tours for organization"
      AxiosResponseHelper.ReportErrors(this._analytics_store, errorMsg, response.data)
    }
  }

  @action
  public async getOrganizationTours(apiInstance: AxiosInstance) {
    console.debug("Fetching organization tours for organization ID:", this.selectedOrganizationId);
    this.isLoading = true;

    await this.getOrganizationToursRaw(apiInstance);

    this.isLoading = false;
  }

  private async getOrganizationToursRaw(apiInstance: AxiosInstance) {
    if (!this.selectedOrganizationId) {
      this.selectedOrganizationTourAssignments = [];
      this.selectedOrganizationTours = [];
      return;
    }
    if (!this.tours || this.tours.length === 0) {
      await this.getTours(apiInstance);
    }
    await this.getOrganizationTourAssignments(apiInstance);

    console.debug(`Fetched assignments: ${this.selectedOrganizationTourAssignments.length}`);

    this.selectedOrganizationTours = this.tours.map(tour => {
      const assignment = this.selectedOrganizationTourAssignments.find(a => a.tourId === tour.id);
      return {
        id: tour.id,
        name: tour.name,
        externalId: tour.externalId,
        organizationId: this.selectedOrganizationId,
        isAssigned: !!assignment
      };
    });
  }

  @action
  public setSelectedOrganizationId(organizationId: string) {
    console.debug("Setting selected organization ID:", organizationId);
    this.selectedOrganizationId = organizationId;
  }

  @action
  public async addTourAssignment(apiInstance: AxiosInstance, tourId: string) {
    this.isUpdating = true;
    const response = await apiInstance.post<IResult<object>>(`api/Tours/organization/${this.selectedOrganizationId}/tour/${tourId}`);
    if (AxiosResponseHelper.IsSuccessfulResponse(response)) {
      await this.getOrganizationToursRaw(apiInstance);
    } else {
      let errorMsg = "Cannot add tour assignment for organization"
      AxiosResponseHelper.ReportErrors(this._analytics_store, errorMsg, response.data)
    }
    this.isUpdating = false;
  }

  @action
  public async deleteTourAssignment(apiInstance: AxiosInstance, tourId: string) {
    this.isUpdating = true;
    const response = await apiInstance.delete<IResult<object>>(`api/Tours/organization/${this.selectedOrganizationId}/tour/${tourId}`);

    if (AxiosResponseHelper.IsSuccessfulResponse(response)) {
      await this.getOrganizationToursRaw(apiInstance);
    } else {
      let errorMsg = "Cannot delete tour assignment for organization"
      AxiosResponseHelper.ReportErrors(this._analytics_store, errorMsg, response.data)
    }
    this.isUpdating = false;
  }
}
